import { Loader2, Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { generatePath, Link } from 'react-router-dom'

import { formatName, type SearchMemberResult, type SearchPatientResult } from '@snootsvet/shared'

import {
  Autocomplete,
  AutocompleteContent,
  AutocompleteEmpty,
  AutocompleteGroup,
  AutocompleteGroupLabel,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
} from '@/frontend/components/autocomplete'
import { Badge } from '@/frontend/components/ui/badge'
import { InputGroupAddon } from '@/frontend/components/ui/input-group'
import { useAppSearch } from '@/frontend/hooks/api/use-app-search'
import { cn } from '@/frontend/lib/utils'
import { routes } from '@/frontend/routes'

type SearchResultItem =
  | { type: 'member'; data: SearchMemberResult }
  | { type: 'patient'; data: SearchPatientResult }

interface SearchResultGroup {
  label: string
  items: SearchResultItem[]
}

function buildGroups(
  data: { members: SearchMemberResult[]; patients: SearchPatientResult[] } | undefined
): SearchResultGroup[] {
  if (!data) return []
  const groups: SearchResultGroup[] = []
  if (data.members.length > 0) {
    groups.push({
      label: 'Members',
      items: data.members.map((m) => ({ type: 'member', data: m })),
    })
  }
  if (data.patients.length > 0) {
    groups.push({
      label: 'Patients',
      items: data.patients.map((p) => ({ type: 'patient', data: p })),
    })
  }
  return groups
}

function itemToString(item: SearchResultItem): string {
  if (item.type === 'member') return formatName(item.data)
  return item.data.name
}

/** Build the profile route for a search result so items can render as links. */
function itemToHref(item: SearchResultItem): string {
  if (item.type === 'patient') {
    return generatePath(routes.patient, { patientId: item.data.id })
  }
  return generatePath(routes.member, { memberId: item.data.id })
}

/** Global search bar for the app header. */
export function AppSearch() {
  const [search, setSearch] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const { data, isLoading } = useAppSearch(search)

  const groups = buildGroups(data)
  const allItems = groups.flatMap((g) => g.items)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <Autocomplete
      value={search}
      onValueChange={setSearch}
      items={allItems}
      mode="none"
      itemToStringValue={itemToString}
    >
      <AutocompleteInput
        ref={inputRef}
        placeholder="Search members or patients..."
        className="h-8 w-80 rounded-none border-0 border-b border-b-border bg-transparent shadow-none ring-0 focus-within:border-b-primary focus-within:ring-0"
        showTrigger={false}
      >
        <InputGroupAddon>
          <Search className="size-3 text-muted-foreground" />
        </InputGroupAddon>
        <InputGroupAddon className="pr-3 text-xs text-muted-foreground" align="inline-end">
          ⌘K
        </InputGroupAddon>
      </AutocompleteInput>
      <AutocompleteContent>
        <AutocompleteList>
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Searching...
            </div>
          ) : (
            <>
              <AutocompleteEmpty>No results found.</AutocompleteEmpty>
              {groups.map((group) => (
                <AutocompleteGroup key={group.label}>
                  <AutocompleteGroupLabel>{group.label}</AutocompleteGroupLabel>
                  {group.items.map((item) => (
                    <AutocompleteItem
                      key={item.data.id}
                      value={item}
                      // Render as a real link so results support open-in-new-tab,
                      // cmd/middle-click, and the right-click context menu. The
                      // Link handles SPA navigation for plain clicks and defers
                      // modified clicks to the browser.
                      render={<Link to={itemToHref(item)} />}
                      onClick={() => setSearch('')}
                    >
                      {item.type === 'member' ? (
                        <div className="flex flex-col">
                          <span
                            className={cn(
                              'flex items-center gap-2 font-medium',
                              item.data.archivedAt && 'text-muted-foreground'
                            )}
                          >
                            <span>
                              {item.data.firstName} {item.data.lastName}
                            </span>
                            {item.data.archivedAt && (
                              <Badge variant="secondary" size="sm">
                                Archived
                              </Badge>
                            )}
                          </span>
                          <span className="text-sm text-muted-foreground">{item.data.email}</span>
                        </div>
                      ) : (
                        <div className="flex flex-col">
                          <span
                            className={cn(
                              'flex items-center gap-2 font-medium',
                              item.data.archivedAt && 'text-muted-foreground'
                            )}
                          >
                            <span>{item.data.name}</span>
                            {item.data.archivedAt && (
                              <Badge variant="secondary" size="sm">
                                Archived
                              </Badge>
                            )}
                            {!item.data.archivedAt && item.data.owner.archivedAt && (
                              <Badge variant="secondary" size="sm">
                                Owner archived
                              </Badge>
                            )}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {item.data.species}
                            {item.data.breed ? ` · ${item.data.breed}` : ''}
                            {' · '}
                            {item.data.owner.firstName} {item.data.owner.lastName}
                          </span>
                        </div>
                      )}
                    </AutocompleteItem>
                  ))}
                </AutocompleteGroup>
              ))}
            </>
          )}
        </AutocompleteList>
      </AutocompleteContent>
    </Autocomplete>
  )
}
